import React, { useEffect, useState } from 'react';
import { useLanguage } from '../components/LanguageContext';
import { ArrowRight, CheckCircle2, Circle, ChevronRight, Pencil, Plus } from 'lucide-react';
interface Props {
  onEnterApp: () => void;
  onGoToProfile: () => void;
  onGoToAddProject: () => void;
  hasProfile?: boolean;
  hasInterests?: boolean;
  hasProject?: boolean;
}
const SUMMIT_START = new Date('2026-05-08T09:00:00+02:00');
function computeDaysLeft(): number {
  const now = new Date();
  const ms = SUMMIT_START.getTime() - now.getTime();
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}
export function PreEventHub({
  onEnterApp,
  onGoToProfile,
  onGoToAddProject,
  hasProfile = false,
  hasInterests = false,
  hasProject = false
}: Props) {
  const {
    t
  } = useLanguage();
  const [mounted, setMounted] = useState(false);
  const daysLeft = computeDaysLeft();
  const allDone = hasProfile && hasInterests && hasProject;
  useEffect(() => {
    const tm = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(tm);
  }, []);
  const fadeUp = (delay: number): React.CSSProperties => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(16px)',
    transition: 'opacity 700ms ease-in-out, transform 800ms cubic-bezier(0.4, 0, 0.2, 1)',
    transitionDelay: `${delay}ms`
  });
  const renderCountdown = () => {
    if (daysLeft > 1) {
      return <>
          <div className="text-white text-[56px] font-[600]" data-id="element-858">{daysLeft}</div>
          <div className="text-[14px] mt-2 text-[#FFFFFF]" data-id="element-859">
            {t('hub.daysLeft')}
          </div>
        </>;
    }
    if (daysLeft === 1) {
      return <>
          <div className="text-[96px] font-bold text-white leading-none" data-id="element-860">1</div>
          <div className="text-[14px] text-[#A0A0A0] mt-2" data-id="element-861">
            {t('hub.oneDay')}
          </div>
        </>;
    }
    if (daysLeft === 0) {
      return <div className="text-[48px] font-bold text-[#C5D82D] leading-tight" data-id="element-862">
          {t('hub.today')}
        </div>;
    }
    return <div className="text-[48px] font-bold text-[#C5D82D] leading-tight" data-id="element-863">
        {t('hub.live')}
      </div>;
  };
  const checklist = [{
    key: 'profile',
    label: t('hub.status.profile'),
    done: hasProfile,
    onClick: onGoToProfile
  }, {
    key: 'interests',
    label: t('hub.status.interests'),
    done: hasInterests,
    onClick: onGoToProfile
  }, {
    key: 'project',
    label: t('hub.status.project'),
    done: hasProject,
    onClick: onGoToAddProject
  }];
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] relative overflow-hidden" data-id="element-864">
      <div className="flex-1 overflow-y-auto px-5 pb-40" style={{
      paddingTop: 'calc(env(safe-area-inset-top) + 40px)'
    }} data-id="element-865">
        {/* SECTION A — Countdown hero */}
        <div className="flex flex-col items-center text-center" style={fadeUp(0)} data-id="element-866">
          <div className="text-[#C5D82D] text-[12px] uppercase tracking-wider font-semibold mb-3" data-id="element-867">
            {t('hub.eyebrow')}
          </div>
          {renderCountdown()}
        </div>

        <div className="text-center mt-4" style={fadeUp(150)} data-id="element-868">
          <div className="text-[14px] text-[#A0A0A0]" data-id="element-869">{t('hub.dates')}</div>
        </div>

        {/* SECTION B — Status card */}
        <div className={`border rounded-[20px] p-5 mt-10 bg-[#141414] ${allDone ? 'border-[#C5D82D]/40' : 'border-[#2A2A2A]'}`} style={fadeUp(300)} data-id="element-870">
          <div className={`text-[13px] font-semibold mb-3 ${allDone ? 'text-[#C5D82D]' : 'text-[#6A6A6A] uppercase tracking-wide'}`} data-id="element-871">
            {allDone ? t('hub.status.allDone') : t('hub.status.title')}
          </div>
          <ul data-id="element-872">
            {checklist.map(item => <li key={item.key} data-id="element-873">
                {item.done ? <div className="flex items-center gap-3 py-2" data-id="element-874">
                    <CheckCircle2 className="w-5 h-5 text-[#C5D82D] shrink-0" strokeWidth={2} data-id="element-875" />
                    <span className="text-[14px] text-white flex-1" data-id="element-876">
                      {item.label}
                    </span>
                  </div> : <button onClick={item.onClick} className="flex items-center gap-3 py-2 w-full text-left active:opacity-70 transition-opacity" data-id="element-877">
                    <Circle className="w-5 h-5 text-[#4A4A4A] shrink-0" strokeWidth={2} data-id="element-878" />
                    <span className="text-[14px] text-[#A0A0A0] flex-1" data-id="element-879">
                      {item.label}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[#6A6A6A] shrink-0" strokeWidth={2} data-id="element-880" />
                  </button>}
              </li>)}
          </ul>
        </div>

        {/* SECTION C — Quick actions */}
        <div className="mt-6 grid grid-cols-2 gap-3" style={fadeUp(450)} data-id="element-881">
          <button onClick={onGoToProfile} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-[14px] py-3 px-3 text-[14px] text-white flex items-center justify-center gap-2 active:bg-[#222] transition-colors" data-id="element-882">
            <Pencil className="w-4 h-4 shrink-0" strokeWidth={2} data-id="element-883" />
            <span className="truncate" data-id="element-884">{t('hub.action.editProfile')}</span>
          </button>
          <button onClick={onGoToAddProject} className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-[14px] py-3 px-3 text-[14px] text-white flex items-center justify-center gap-2 active:bg-[#222] transition-colors" data-id="element-885">
            <Plus className="w-4 h-4 shrink-0" strokeWidth={2} data-id="element-886" />
            <span className="truncate" data-id="element-887">{t('hub.action.addProject')}</span>
          </button>
        </div>
      </div>

      {/* Bottom CTA */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#0D0D0D]/95 backdrop-blur-xl px-5 pt-4 z-40" style={{
      paddingBottom: 'calc(env(safe-area-inset-bottom) + 24px)',
      ...fadeUp(450)
    }} data-id="element-888">
        <button onClick={onEnterApp} className="w-full py-4 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold tracking-wide flex items-center justify-center gap-2 transition-colors hover:bg-[#d4e63a]" data-id="element-889">
          {t('hub.enterNow')}
          <ArrowRight className="w-4 h-4" strokeWidth={2} data-id="element-890" />
        </button>
      </div>
    </div>;
}